import { useTranslation } from 'react-i18next';
import MedicationIcon from 'components/atoms/icons/medicationIcon';
import BaseButton from 'components/atoms/baseButton';
import { type FC } from 'react';
import EmptyTablePlaceholder from 'components/atoms/base-empty-table-placeholder';
import BucketIcon from 'components/atoms/icons/bucketIcon';
import BaseLoader from 'components/atoms/base-loader';
import AddMedicationForm from 'components/molecules/admin-panel/forms/medication';
import BasePagination from 'components/atoms/base-pagination';
import BaseSelectBox from 'components/atoms/base-select-box';
import PlusIcon from 'components/atoms/icons/plus';
import { isCombinedModule } from 'infrastructure/functions';
import { useCheckRoleAccess } from 'infrastructure/hooks';

import s from './styles.module.scss';
import MedicationItem from './components/medication-item';
import { useMedication } from './hooks/use-medication';

import type { ModuleType } from 'infrastructure/enums';

interface PatientMedicationProps {
  patientGuid: string;
  module: ModuleType;
  sessionId?: string | null;
}

const PatientMedication: FC<PatientMedicationProps> = ({
  patientGuid,
  module,
  sessionId,
}) => {
  const { t } = useTranslation();
  const {
    modalTitle,
    Alert,
    open,
    modifyItemGuid,
    medication,
    isLoading,
    isFetching,
    addLoading,
    editLoading,
    deleteLoading,
    loading,
    disableButton,
    wrapperRef,
    OPTIONS,
    status,
    page,
    itemPerPage,
    medicationForUpdate,
    showTrackingPlatform,
    onCancelForm,
    handleDelete,
    handleEdit,
    scrollToTop,
    onSubmit,
    setStatus,
    setPage,
    setItemPerPage,
    handleOpen,
    remove,
  } = useMedication({ patientGuid, module, sessionId });
  const { isReadOnlyRole } = useCheckRoleAccess();

  const disableActions = isCombinedModule(module)
    ? isReadOnlyRole
    : disableButton;

  return (
    <>
      <div className={s.wrapper} ref={wrapperRef} id="medication">
        <div className={s.header}>
          <h4 className={s.title}>
            <MedicationIcon />
            {t('labels.medication')}
          </h4>
          <div className={s['header-select-wrapper']}>
            <div className={s['header-select']}>
              <p data-cy="medication-status-filter">{t('labels.status')}</p>
              <BaseSelectBox
                value={status}
                onChange={(v) => {
                  remove();
                  setPage(1);
                  setStatus(v);
                }}
                options={OPTIONS}
                minWidth="110px"
              />
            </div>
          </div>
          <BaseButton
            dataCy="addMedication-button"
            icon={<PlusIcon color="transparent" />}
            label={`${t('controls.addMedication')}`}
            onClick={handleOpen}
            loading={addLoading}
            disabled={disableActions || loading}
          />
        </div>
        <div className={s.divider} />
        <BaseLoader loading={isLoading || isFetching}>
          <div className={s.body} style={{ position: 'relative' }}>
            {!!medication?.items.length && (
              <div className={s['items-wrapper']}>
                {medication?.items.map((item) => {
                  return (
                    <MedicationItem
                      key={item.guid}
                      item={item}
                      disableActions={disableActions}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      loading={deleteLoading || editLoading}
                      modifyItemGuid={modifyItemGuid}
                    />
                  );
                })}
              </div>
            )}
            {medication?.items.length === 0 && (
              <EmptyTablePlaceholder
                text={t('labels.medicationHistoryIsEmpty')}
                icon={<BucketIcon width={32} height={32} />}
              />
            )}
            <BasePagination
              page={page}
              total={medication?.count}
              align="center"
              pageSizeOptions={[5, 10, 15, 20, 25, 30, 100]}
              onChange={(newPage) => {
                scrollToTop();
                setPage(newPage);
              }}
              showSizeChanger
              pageSize={itemPerPage}
              onShowSizeChange={(current, size) => {
                scrollToTop();
                setItemPerPage(size);
              }}
              hide={(medication?.count ?? 0) < 5}
            />
          </div>
        </BaseLoader>
      </div>

      <AddMedicationForm
        loading={addLoading || editLoading}
        onSubmit={onSubmit}
        onCancelForm={onCancelForm}
        defaultValues={medicationForUpdate}
        modalTitle={modalTitle}
        open={open}
        showTrackingPlatform={!medicationForUpdate && showTrackingPlatform}
      />
      {Alert}
    </>
  );
};
export default PatientMedication;
